import type { ProcessedOption } from "../components/types/store-types"

/**
 * Minimum interface required for pattern grouping
 */
export interface PatternGroupable {
  id: string
  label: string
  value: string
  compatibility?: Array<{ key: string; values: string }>
}

export interface GroupedPatterns<T extends PatternGroupable = ProcessedOption> {
  key: string
  name: string
  items: T[]
}

/**
 * Groups pattern options into three categories for UI rendering
 * Matches the exact logic from PatternLayout component
 *
 * Priority: compatibility array first, then label fallback
 *
 * @param patterns - Array of pattern items (ProcessedOption or compatible interface)
 * @returns Array of grouped pattern sections
 */
export const groupPatternOptions = <T extends PatternGroupable>(
  patterns: T[]
): GroupedPatterns<T>[] => {
  const factory = patterns.filter((p) => {
    // Check compatibility first (higher priority)
    const compatMatch = p.compatibility?.find((c) => {
      const key = c.key?.toLowerCase()
      return key === "factory" && c.values === "Factory Pattern"
    })
    if (compatMatch) return true

    // Check for other compat types (excludes this pattern)
    const hasOtherCompat = p.compatibility?.some((c) => {
      const key = c.key?.toLowerCase()
      return (
        key === "design-pattern" ||
        (key === "pattern" && c.values !== "Factory Pattern") ||
        key === "perforation" ||
        c.values.includes("Perforation")
      )
    })
    if (hasOtherCompat) return false

    // Fallback to label
    return p.label === "Factory Pattern"
  })

  const perforation = patterns.filter((p) => {
    // Check compatibility first
    const compatMatch = p.compatibility?.find((c) => {
      const key = c.key?.toLowerCase()
      return key === "perforation" || c.values.includes("Perforation")
    })
    if (compatMatch) return true

    // Check for other compat types (excludes this pattern)
    const hasOtherCompat = p.compatibility?.some((c) => {
      const key = c.key?.toLowerCase()
      return (
        (key === "factory" && c.values === "Factory Pattern") ||
        key === "design-pattern" ||
        (key === "pattern" && c.values !== "Factory Pattern")
      )
    })
    if (hasOtherCompat) return false

    // Fallback to label/value
    return p.label.includes("Perforation") || p.value.includes("perf")
  })

  const designPattern = patterns.filter((p) => {
    // Check compatibility first
    const compatMatch = p.compatibility?.find((c) => {
      const key = c.key?.toLowerCase()
      return (
        key === "design-pattern" ||
        (key === "pattern" && c.values !== "Factory Pattern")
      )
    })
    if (compatMatch) return true

    // Check for other compat types (excludes this pattern)
    const hasOtherCompat = p.compatibility?.some((c) => {
      const key = c.key?.toLowerCase()
      return (
        (key === "factory" && c.values === "Factory Pattern") ||
        key === "perforation" ||
        c.values.includes("Perforation")
      )
    })
    if (hasOtherCompat) return false

    // Fallback: not factory, not perforation
    return (
      p.label !== "Factory Pattern" &&
      !p.label.includes("Perforation") &&
      !p.value.includes("perf")
    )
  })

  return [
    { key: "factory", name: "Factory Pattern", items: factory },
    { key: "design-pattern", name: "Design Patterns", items: designPattern },
    { key: "perforation", name: "Perforations", items: perforation },
  ].filter((group) => group.items.length > 0)
}
